import
{
    Api,
    ArrowBack,
    Business,
    Cloud,
    Email,
    Info,
    LocationOn,
    Phone,
    Security,
    SmartToy,
    Storage,
} from '@mui/icons-material';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Container } from '@src/App/Components/Core/Layouts/LayoutElements/Container/Container';
import Content from '@src/App/Components/Core/Layouts/LayoutElements/Content/Content';
import ContentHeader from '@src/App/Components/Core/Layouts/LayoutElements/ContentHeader/ContentHeader';
import { isLoggedIn } from '@store/reducer/clientReducer';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

function Datenschutz()
{
    const theme = useTheme();
    const secret = useSelector((state: AppState) => state.client.secret);
    const loggedIn = isLoggedIn(secret);

    return (
        <Content contentHeader={<ContentHeader title="Datenschutzerklärung" />}>
            <Container>
                <Paper
                    elevation={0}
                    sx={{
                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        borderRadius: 3,
                        p: 4,
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '2px',
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                        },
                    }}
                >
                    <Typography
                        component="button"
                        onClick={() => window.history.back()}
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            textDecoration: 'none',
                            fontSize: '0.75rem',
                            mb: 2,
                            display: 'block',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 0,
                            textAlign: 'left',
                            '&:hover': {
                                color: 'primary.main',
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        ← Zurück
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: 2,
                                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                            }}
                        >
                            <Security />
                        </Box>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                            Datenschutzerklärung
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Business sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    1. Verantwortlicher
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Verantwortlicher für die Datenverarbeitung auf dieser Website ist:
                                    <br />
                                    Talentillion GmbH
                                    <br />
                                    Kronenstr. 18
                                    <br />
                                    10117 Berlin
                                    <br />
                                    Deutschland
                                    <br />
                                    <br />
                                    E-Mail: inbox@talentillion.de
                                    <br />
                                    Telefon: +49-30-959998170
                                    <br />
                                    <br />
                                    Vertreten durch die Geschäftsführer: Sebastian Ullrich, Andreas Sass
                                    <br />
                                    Registergericht: Amtsgericht Charlottenburg, HRB 278450 B
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Email sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    2. Erhebung und Speicherung personenbezogener Daten
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary', mb: 2 }}>
                                    Wir erheben und verwenden Ihre personenbezogenen Daten nur, soweit dies gesetzlich
                                    erlaubt ist oder Sie in die Datenerhebung einwilligen.
                                </Typography>
                                <Typography variant="body2" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    <strong>Bei der Nutzung unserer Anwendung werden folgende Daten erhoben:</strong>
                                    <br />• Benutzerdaten (Name, E-Mail-Adresse, Telefonnummer)
                                    <br />• Berufliche Informationen (Skills, Zertifikate, Rollen, Kompetenzen)
                                    <br />• Unternehmensdaten (Organisationsstruktur, Projekte, Kunden)
                                    <br />• Nutzungsdaten (Login-Zeiten, Aktivitäten in der Anwendung)
                                    <br />• Technische Daten (IP-Adresse, Browser-Informationen, Gerätedaten)
                                    <br />• Hochgeladene Dateien (CVs, Zertifikate, Dokumente)
                                    <br />• Chat-Nachrichten und KI-Interaktionen
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Cloud sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    3. Cloud-Infrastruktur und Hosting
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    <strong>Amazon Web Services (AWS):</strong>
                                    <br />
                                    Unsere Anwendung wird vollständig auf AWS-Infrastruktur in der Region eu-central-1
                                    (Frankfurt) gehostet. Dabei werden folgende AWS-Services genutzt:
                                    <br />• AWS Lambda (Serverless Computing)
                                    <br />• Amazon DynamoDB (Datenbank)
                                    <br />• Amazon S3 (Dateispeicherung)
                                    <br />• Amazon CloudFront (Content Delivery Network)
                                    <br />• AWS Cognito (Benutzerauthentifizierung)
                                    <br />• AWS API Gateway (API-Management)
                                    <br />• AWS KMS (Verschlüsselung)
                                    <br />
                                    <br />
                                    Alle Daten werden verschlüsselt gespeichert und übertragen. AWS ist DSGVO-konform
                                    und bietet entsprechende Auftragsverarbeitungsverträge.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <SmartToy sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    4. Künstliche Intelligenz und OpenAI
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    <strong>OpenAI GPT-Modelle:</strong>
                                    <br />
                                    Unsere Anwendung nutzt OpenAI GPT-Modelle (GPT-3.5-turbo, GPT-4, GPT-4o,
                                    GPT-4o-mini) für folgende Funktionen:
                                    <br />• CV-Analyse und Skill-Extraktion
                                    <br />• Chat-Assistent und Beratungsfunktionen
                                    <br />• Automatische Kategorisierung von Skills und Kompetenzen
                                    <br />• Generierung von Metadaten für Zertifikate und Rollen
                                    <br />
                                    <br />
                                    <strong>Datenverarbeitung:</strong>
                                    <br />• Texte werden zur Analyse an OpenAI übermittelt
                                    <br />• OpenAI speichert diese Daten nicht dauerhaft (Zero Data Retention)
                                    <br />• Personenbezogene Daten werden vor der Übermittlung anonymisiert
                                    <br />• Die Verarbeitung erfolgt auf Basis Ihrer Einwilligung (Art. 6 Abs. 1 lit. a
                                    DSGVO)
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Api sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    5. Externe Integrationen
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    <strong>Microsoft Graph API:</strong>
                                    <br />
                                    Bei aktivierter Microsoft-Integration werden E-Mails über Microsoft Graph versendet.
                                    Hierfür werden Ihre Microsoft-Anmeldedaten und E-Mail-Adressen verarbeitet.
                                    <br />
                                    <br />
                                    <strong>Zeiterfassungssysteme:</strong>
                                    <br />• <strong>Clockodo:</strong> Synchronisation von Arbeitszeiten, Projekten und
                                    Abwesenheiten
                                    <br />• <strong>ZEP:</strong> Synchronisation von Arbeitszeiten, Projekten und
                                    Abwesenheiten
                                    <br />
                                    <br />
                                    <strong>Slack-Integration:</strong>
                                    <br />
                                    Bei aktivierter Slack-Integration werden Nachrichten und Benachrichtigungen über
                                    Slack versendet.
                                    <br />
                                    <br />
                                    <strong>OpenHolidays API:</strong>
                                    <br />
                                    Abruf von Feiertagen zur Kalenderberechnung (keine personenbezogenen Daten).
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Storage sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    6. Datenspeicherung und -sicherheit
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    <strong>Verschlüsselung:</strong>
                                    <br />• Alle Daten werden mit AWS KMS verschlüsselt gespeichert
                                    <br />• Übertragung erfolgt ausschließlich über HTTPS/TLS
                                    <br />• Zertifikate und private Schlüssel werden sicher in AWS Parameter Store
                                    gespeichert
                                    <br />
                                    <br />
                                    <strong>Backup und Wiederherstellung:</strong>
                                    <br />• Automatische Backups in separaten AWS S3 Buckets
                                    <br />• Versionierung von Datenbankinhalten
                                    <br />• Disaster Recovery Mechanismen
                                    <br />
                                    <br />
                                    <strong>Zugriffskontrolle:</strong>
                                    <br />• Rollenbasierte Berechtigungen
                                    <br />• Multi-Faktor-Authentifizierung
                                    <br />• Audit-Logs für alle Zugriffe
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    7. Zweck der Datenverarbeitung
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Die Verarbeitung der personenbezogenen Daten dient folgenden Zwecken:
                                    <br />• Bereitstellung der Skill-Management-Plattform
                                    <br />• Verwaltung von Mitarbeiterkompetenzen und -qualifikationen
                                    <br />• Projektplanung und Ressourcenallokation
                                    <br />• Personalentwicklung und Weiterbildungsplanung
                                    <br />• Berichterstattung und Analysen
                                    <br />• Kommunikation und Benachrichtigungen
                                    <br />• Gewährleistung der Systemsicherheit und -stabilität
                                    <br />• Erfüllung rechtlicher Verpflichtungen
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Phone sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    8. Rechtsgrundlagen
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Die Verarbeitung erfolgt auf Grundlage folgender Rechtsgrundlagen:
                                    <br />• <strong>Art. 6 Abs. 1 lit. a DSGVO:</strong> Einwilligung (KI-Funktionen,
                                    externe Integrationen)
                                    <br />• <strong>Art. 6 Abs. 1 lit. b DSGVO:</strong> Vertragserfüllung
                                    (Bereitstellung der Plattform)
                                    <br />• <strong>Art. 6 Abs. 1 lit. c DSGVO:</strong> Rechtliche Verpflichtung
                                    (Aufbewahrungsfristen)
                                    <br />• <strong>Art. 6 Abs. 1 lit. f DSGVO:</strong> Berechtigtes Interesse
                                    (Systemsicherheit, Optimierung)
                                    <br />• <strong>Art. 9 Abs. 2 lit. a DSGVO:</strong> Ausdrückliche Einwilligung
                                    (besondere Kategorien)
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Business sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    9. Speicherdauer
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    <strong>Benutzerdaten:</strong> Solange das Benutzerkonto aktiv ist
                                    <br />
                                    <strong>Berufliche Daten:</strong> Solange für die Zweckerfüllung erforderlich
                                    <br />
                                    <strong>Log-Daten:</strong> 30 Tage (AWS CloudWatch)
                                    <br />
                                    <strong>Backup-Daten:</strong> Bis zu 7 Jahre für Disaster Recovery
                                    <br />
                                    <strong>Chat-Verläufe:</strong> Bis zur Löschung durch den Benutzer
                                    <br />
                                    <strong>Hochgeladene Dateien:</strong> Solange für die Zweckerfüllung erforderlich
                                    <br />
                                    <br />
                                    Nach Ablauf der Speicherdauer werden die Daten automatisch gelöscht, es sei denn,
                                    gesetzliche Aufbewahrungsfristen erfordern eine längere Speicherung.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Email sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    10. Ihre Rechte
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Sie haben folgende Rechte bezüglich Ihrer personenbezogenen Daten:
                                    <br />• <strong>Recht auf Auskunft</strong> (Art. 15 DSGVO)
                                    <br />• <strong>Recht auf Berichtigung</strong> (Art. 16 DSGVO)
                                    <br />• <strong>Recht auf Löschung</strong> (Art. 17 DSGVO)
                                    <br />• <strong>Recht auf Einschränkung der Verarbeitung</strong> (Art. 18 DSGVO)
                                    <br />• <strong>Recht auf Datenübertragbarkeit</strong> (Art. 20 DSGVO)
                                    <br />• <strong>Recht auf Widerspruch</strong> (Art. 21 DSGVO)
                                    <br />• <strong>Recht auf Widerruf der Einwilligung</strong> (Art. 7 Abs. 3 DSGVO)
                                    <br />
                                    <br />
                                    Zur Ausübung Ihrer Rechte wenden Sie sich bitte an: inbox@talentillion.de
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <LocationOn sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    11. Datenübermittlung an Drittländer
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    <strong>OpenAI (USA):</strong>
                                    <br />
                                    Für KI-Funktionen werden Daten an OpenAI in die USA übermittelt. OpenAI ist Privacy
                                    Shield zertifiziert und bietet angemessene Datenschutzgarantien.
                                    <br />
                                    <br />
                                    <strong>AWS (EU):</strong>
                                    <br />
                                    Alle anderen Daten verbleiben in der AWS-Region eu-central-1 (Frankfurt,
                                    Deutschland) und werden nicht in Drittländer übermittelt.
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Security sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    12. Beschwerderecht
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Sie haben das Recht, sich bei einer Datenschutz-Aufsichtsbehörde über die
                                    Verarbeitung Ihrer personenbezogenen Daten durch uns zu beschweren.
                                    <br />
                                    <br />
                                    <strong>Zuständige Aufsichtsbehörde:</strong>
                                    <br />
                                    Berliner Beauftragte für Datenschutz und Informationsfreiheit
                                    <br />
                                    Friedrichstr. 219
                                    <br />
                                    10969 Berlin
                                    <br />
                                    E-Mail: mailbox@datenschutz-berlin.de
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                            <Business sx={{ color: 'primary.main', mt: 0.5 }} />
                            <Box>
                                <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                                    13. Aktualität der Datenschutzerklärung
                                </Typography>
                                <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.secondary' }}>
                                    Diese Datenschutzerklärung ist aktuell gültig und hat den Stand Januar 2025. Durch
                                    die Weiterentwicklung unserer Website oder aufgrund geänderter gesetzlicher
                                    Bestimmungen kann es notwendig werden, diese Datenschutzerklärung zu ändern. Die
                                    jeweils aktuelle Datenschutzerklärung kann jederzeit auf der Website unter
                                    /datenschutz abgerufen werden.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>
            </Container>
        </Content>
    );
}

export default Datenschutz;
